import { useEffect, useState } from "react";
import { useI18n } from "@/i18n";
import { useAuth } from "@/features/auth/AuthContext";
import { supabase } from "@/lib/supabase";
import { api } from "@/lib/api";
import { uploadUserFile, removeByPublicUrl } from "@/lib/storage";
import {
  ARCHETYPES,
  ARCHETYPE_LABELS_AR,
  BIO_MAX,
  DOCTOR_PHOTOS_MAX,
  REFERENCE_DESIGNS_MAX,
  type Archetype,
} from "@/lib/constants";
import { TagInput } from "@/components/ui/TagInput";
import { ImageDeleteGuard } from "@/components/ui/ImageDeleteGuard";
import { ArchetypeQuiz } from "./ArchetypeQuiz";
import { useProfileForm } from "./useProfileForm";
import type {
  CanvasBackground,
  Cohort,
  DoctorPhoto,
  ReferenceDesign,
  Specialty,
} from "@/lib/database.types";

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/** Polls a generation job until it finishes; throws on failure. */
async function pollUntilDone(jobId: string): Promise<void> {
  for (let i = 0; i < 80; i++) {
    await sleep(3000);
    const s = await api.pollJob(jobId);
    if (s.status === "done") return;
    if (s.status === "failed") throw new Error(s.error ?? "failed");
  }
  throw new Error("timeout");
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="card mb-4">
      <h2 className="mb-4 text-base font-semibold">{title}</h2>
      {children}
    </section>
  );
}

export function ProfilePage() {
  const { t, lang } = useI18n();
  const { session } = useAuth();
  const { draft, patch, saveState } = useProfileForm();
  const uid = session!.user.id;

  const [specialties, setSpecialties] = useState<Specialty[]>([]);
  const [references, setReferences] = useState<ReferenceDesign[]>([]);
  const [photos, setPhotos] = useState<DoctorPhoto[]>([]);
  const [quizOpen, setQuizOpen] = useState(false);
  const [deleting, setDeleting] = useState<{ url: string; run: () => void } | null>(null);
  // ---- canvas assets ----
  const [backgrounds, setBackgrounds] = useState<CanvasBackground[]>([]);
  const [characterSheetUrl, setCharacterSheetUrl] = useState<string | null>(null);
  const [assetBusy, setAssetBusy] = useState<string | null>(null); // 'charsheet' | `cutout:<id>` | 'bg'
  const [assetError, setAssetError] = useState<string | null>(null);
  const [bgPrompt, setBgPrompt] = useState("");

  useEffect(() => {
    supabase.from("specialties").select("*").order("name").then(({ data }) =>
      setSpecialties(data ?? []),
    );
    supabase
      .from("reference_designs")
      .select("*")
      .eq("user_id", uid)
      .then(({ data }) => setReferences(data ?? []));
    supabase
      .from("doctor_photos")
      .select("*")
      .eq("user_id", uid)
      .then(({ data }) => setPhotos(data ?? []));
    supabase
      .from("canvas_backgrounds")
      .select("*")
      .eq("user_id", uid)
      .order("created_at", { ascending: false })
      .then(({ data }) => setBackgrounds((data as CanvasBackground[]) ?? []));
    supabase
      .from("profiles")
      .select("character_sheet_url")
      .eq("id", uid)
      .maybeSingle()
      .then(({ data }) => setCharacterSheetUrl(data?.character_sheet_url ?? null));
  }, [uid]);

  if (!draft) return <p className="text-muted">{t("loading")}</p>;

  // ---- archetypes ----
  function toggleArchetype(a: Archetype) {
    const cur = draft!.archetypes as Archetype[];
    if (cur.includes(a)) {
      patch({ archetypes: cur.filter((x) => x !== a) });
    } else if (cur.length < 3) {
      patch({ archetypes: [...cur, a] });
    }
  }

  // ---- cohorts ----
  function updateCohort(i: number, c: Partial<Cohort>) {
    const next = [...draft!.target_cohorts];
    next[i] = { ...next[i], ...c };
    patch({ target_cohorts: next });
  }
  function addCohort() {
    patch({
      target_cohorts: [...draft!.target_cohorts, { gender: "Both", complaints: "" }],
    });
  }
  function removeCohort(i: number) {
    patch({ target_cohorts: draft!.target_cohorts.filter((_, x) => x !== i) });
  }

  // ---- specialty add ----
  async function addSpecialty() {
    const name = prompt(t("addSpecialty"));
    if (!name?.trim()) return;
    const { data, error } = await supabase
      .from("specialties")
      .insert({ name: name.trim(), created_by: uid })
      .select()
      .single();
    if (!error && data) {
      setSpecialties((s) => [...s, data]);
      patch({ specialty_id: data.id });
    }
  }

  // ---- logo ----
  async function onLogoUpload(file: File) {
    const url = await uploadUserFile("logos", uid, file);
    patch({ logo_url: url });
  }

  // ---- references (with style extraction) ----
  async function onReferenceUpload(file: File) {
    if (references.length >= REFERENCE_DESIGNS_MAX) return;
    const url = await uploadUserFile("references", uid, file);
    const { data } = await supabase
      .from("reference_designs")
      .insert({ user_id: uid, image_url: url })
      .select()
      .single();
    const next = data ? [...references, data] : references;
    setReferences(next);
    // Re-extract brand style from all references (PRD §5.5).
    try {
      const style = await api.extractStyle({ referenceUrls: next.map((r) => r.image_url) });
      patch({ brand_style_notes: style });
    } catch {
      /* non-blocking */
    }
  }

  // ---- doctor photos ----
  async function onPhotoUpload(file: File) {
    if (photos.length >= DOCTOR_PHOTOS_MAX) return;
    const url = await uploadUserFile("doctor-photos", uid, file);
    const { data } = await supabase
      .from("doctor_photos")
      .insert({ user_id: uid, image_url: url })
      .select()
      .single();
    if (data) setPhotos((p) => [...p, data]);
  }

  // ---- canvas assets: character sheet (2cr) ----
  async function generateCharacterSheet() {
    setAssetBusy("charsheet");
    setAssetError(null);
    try {
      const { jobId } = await api.generateCharacterSheet();
      await pollUntilDone(jobId);
      const { data } = await supabase
        .from("profiles")
        .select("character_sheet_url")
        .eq("id", uid)
        .maybeSingle();
      setCharacterSheetUrl(data?.character_sheet_url ?? null);
    } catch (e) {
      setAssetError(e instanceof Error ? e.message : t("error"));
    } finally {
      setAssetBusy(null);
    }
  }

  // ---- canvas assets: backgrounds (upload free / generate 1cr) ----
  async function onBackgroundUpload(file: File) {
    const url = await uploadUserFile("canvas-backgrounds", uid, file);
    const { data } = await supabase
      .from("canvas_backgrounds")
      .insert({ user_id: uid, image_url: url, source: "upload" })
      .select()
      .single();
    if (data) setBackgrounds((b) => [data as CanvasBackground, ...b]);
  }

  async function generateBackground() {
    setAssetBusy("bg");
    setAssetError(null);
    try {
      const { jobId } = await api.generateCanvasBackground({ prompt: bgPrompt });
      await pollUntilDone(jobId);
      const { data } = await supabase
        .from("canvas_backgrounds")
        .select("*")
        .eq("user_id", uid)
        .order("created_at", { ascending: false });
      setBackgrounds((data as CanvasBackground[]) ?? []);
      setBgPrompt("");
    } catch (e) {
      setAssetError(e instanceof Error ? e.message : t("error"));
    } finally {
      setAssetBusy(null);
    }
  }

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-xl font-bold">{t("profileTitle")}</h1>
        <span className="text-xs text-muted">
          {saveState === "saving" && t("saving")}
          {saveState === "saved" && `✓ ${t("saved")}`}
          {saveState === "error" && t("error")}
        </span>
      </div>

      {/* Entity & contact */}
      <Section title={t("entityContact")}>
        <label className="label">{t("displayName")}</label>
        <input
          className="input mb-3"
          value={draft.display_name ?? ""}
          onChange={(e) => patch({ display_name: e.target.value })}
        />
        <label className="label">{t("clinicPhone")}</label>
        <input
          className="input mb-3"
          dir="ltr"
          value={draft.phone ?? ""}
          onChange={(e) => patch({ phone: e.target.value })}
        />
        <label className="label">{t("clinicAddress")}</label>
        <input
          className="input mb-3"
          value={draft.clinic_address ?? ""}
          onChange={(e) => patch({ clinic_address: e.target.value })}
        />
        <label className="label">{t("bio")}</label>
        <textarea
          className="input min-h-24"
          maxLength={BIO_MAX}
          value={draft.bio ?? ""}
          onChange={(e) => patch({ bio: e.target.value })}
        />
        <div className="mt-1 text-end text-xs text-muted">
          {(draft.bio ?? "").length} / {BIO_MAX}
        </div>
      </Section>

      {/* Specialty & expertise */}
      <Section title={t("specialty")}>
        <div className="mb-3 flex gap-2">
          <select
            className="input"
            value={draft.specialty_id ?? ""}
            onChange={(e) => patch({ specialty_id: e.target.value || null })}
          >
            <option value="">—</option>
            {specialties.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
                {!s.approved ? " ⏳" : ""}
              </option>
            ))}
          </select>
          <button className="btn-ghost px-3" onClick={addSpecialty}>
            +
          </button>
        </div>

        <label className="label">{t("scope")}</label>
        <textarea
          className="input mb-4 min-h-16"
          value={draft.scope_of_expertise ?? ""}
          onChange={(e) => patch({ scope_of_expertise: e.target.value })}
        />

        <label className="label">{t("services")}</label>
        <TagInput
          tags={draft.services}
          onChange={(services) => patch({ services })}
        />

        <div className="mt-4 flex items-center gap-2">
          <button
            className={draft.focus_mode === "general" ? "btn-primary flex-1" : "btn-ghost flex-1"}
            onClick={() => patch({ focus_mode: "general" })}
          >
            {t("focusGeneral")}
          </button>
          <button
            className={draft.focus_mode === "specific" ? "btn-primary flex-1" : "btn-ghost flex-1"}
            onClick={() => patch({ focus_mode: "specific" })}
          >
            {t("focusSpecific")}
          </button>
        </div>
        {draft.focus_mode === "specific" && (
          <div className="mt-3">
            <label className="label">{t("focusSpecific")}</label>
            <div className="flex flex-wrap gap-2">
              {draft.services.map((s) => {
                const on = draft.focus_services.includes(s);
                return (
                  <button
                    key={s}
                    className={`rounded-full px-3 py-1 text-sm ${on ? "bg-accent text-accent-fg" : "bg-slate-100 text-muted"}`}
                    onClick={() =>
                      patch({
                        focus_services: on
                          ? draft.focus_services.filter((x) => x !== s)
                          : [...draft.focus_services, s],
                      })
                    }
                  >
                    {s}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </Section>

      {/* Target cohorts */}
      <Section title={t("cohorts")}>
        {draft.target_cohorts.map((c, i) => (
          <div key={i} className="mb-3 rounded-xl border border-slate-100 p-3">
            <div className="mb-2 flex gap-2">
              <input
                type="number"
                className="input"
                placeholder={t("ageFrom")}
                value={c.age_from ?? ""}
                onChange={(e) => updateCohort(i, { age_from: Number(e.target.value) })}
              />
              <input
                type="number"
                className="input"
                placeholder={t("ageTo")}
                value={c.age_to ?? ""}
                onChange={(e) => updateCohort(i, { age_to: Number(e.target.value) })}
              />
              <select
                className="input"
                value={c.gender ?? "Both"}
                onChange={(e) => updateCohort(i, { gender: e.target.value as Cohort["gender"] })}
              >
                <option value="Male">{t("male")}</option>
                <option value="Female">{t("female")}</option>
                <option value="Both">{t("both")}</option>
              </select>
            </div>
            <textarea
              className="input min-h-14"
              placeholder={t("complaints")}
              value={c.complaints ?? ""}
              onChange={(e) => updateCohort(i, { complaints: e.target.value })}
            />
            <button className="mt-2 text-sm text-red-500" onClick={() => removeCohort(i)}>
              {t("removeCohort")}
            </button>
          </div>
        ))}
        <button className="btn-ghost w-full" onClick={addCohort}>
          + {t("addCohort")}
        </button>
      </Section>

      {/* Archetypes */}
      <Section title={t("archetypes")}>
        <div className="mb-3 flex flex-wrap gap-2">
          {ARCHETYPES.map((a) => {
            const on = (draft.archetypes as Archetype[]).includes(a);
            return (
              <button
                key={a}
                className={`rounded-full px-3 py-1 text-sm ${on ? "bg-accent text-accent-fg" : "bg-slate-100 text-muted"}`}
                onClick={() => toggleArchetype(a)}
              >
                {lang === "ar" ? ARCHETYPE_LABELS_AR[a] : a}
              </button>
            );
          })}
        </div>
        <button className="btn-ghost w-full" onClick={() => setQuizOpen(true)}>
          ✦ {t("findArchetypes")}
        </button>
        <ArchetypeQuiz
          open={quizOpen}
          onClose={() => setQuizOpen(false)}
          onResult={(top3) => patch({ archetypes: top3 })}
        />
      </Section>

      {/* Branding */}
      <Section title={t("branding")}>
        {/* Logo */}
        <label className="label">{t("logo")}</label>
        <div className="mb-2 flex items-center gap-3">
          {draft.logo_url ? (
            <img
              src={draft.logo_url}
              alt="logo"
              className="h-16 w-16 rounded-xl object-contain ring-1 ring-slate-100"
            />
          ) : (
            <div className="h-16 w-16 rounded-xl bg-slate-100" />
          )}
          <label className="btn-ghost cursor-pointer">
            {t("uploadLogo")}
            <input
              type="file"
              accept="image/*"
              hidden
              onChange={(e) => e.target.files?.[0] && onLogoUpload(e.target.files[0])}
            />
          </label>
          {draft.logo_url && (
            <button
              className="text-sm text-red-500"
              onClick={() =>
                setDeleting({
                  url: draft.logo_url!,
                  run: async () => {
                    await removeByPublicUrl("logos", draft.logo_url!);
                    patch({ logo_url: null });
                    setDeleting(null);
                  },
                })
              }
            >
              {t("removeLogo")}
            </button>
          )}
        </div>
        <div className="mb-4 flex gap-2">
          <button
            className={draft.logo_lang === "ar" ? "btn-primary flex-1" : "btn-ghost flex-1"}
            onClick={() => patch({ logo_lang: "ar" })}
          >
            {t("logoArabic")}
          </button>
          <button
            className={draft.logo_lang === "en" ? "btn-primary flex-1" : "btn-ghost flex-1"}
            onClick={() => patch({ logo_lang: "en" })}
          >
            {t("logoEnglish")}
          </button>
        </div>

        {/* Reference designs */}
        <label className="label">
          {t("referenceDesigns")} ({references.length}/{REFERENCE_DESIGNS_MAX})
        </label>
        <div className="mb-4 flex flex-wrap gap-2">
          {references.map((r) => (
            <div key={r.id} className="relative">
              <img src={r.image_url} alt="" className="h-20 w-20 rounded-xl object-cover" />
              <button
                className="absolute -top-2 -end-2 rounded-full bg-red-500 px-2 text-xs text-white"
                onClick={() =>
                  setDeleting({
                    url: r.image_url,
                    run: async () => {
                      await removeByPublicUrl("references", r.image_url);
                      await supabase.from("reference_designs").delete().eq("id", r.id);
                      setReferences((x) => x.filter((y) => y.id !== r.id));
                      setDeleting(null);
                    },
                  })
                }
              >
                ×
              </button>
            </div>
          ))}
          {references.length < REFERENCE_DESIGNS_MAX && (
            <label className="flex h-20 w-20 cursor-pointer items-center justify-center rounded-xl bg-slate-100 text-2xl text-muted">
              +
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={(e) => e.target.files?.[0] && onReferenceUpload(e.target.files[0])}
              />
            </label>
          )}
        </div>

        {/* Doctor photos */}
        <label className="label">
          {t("doctorPhotos")} ({photos.length}/{DOCTOR_PHOTOS_MAX})
        </label>
        <div className="flex flex-wrap gap-2">
          {photos.map((p) => (
            <div key={p.id} className="relative">
              <img src={p.image_url} alt="" className="h-20 w-20 rounded-xl object-cover" />
              <button
                className="absolute -top-2 -end-2 rounded-full bg-red-500 px-2 text-xs text-white"
                onClick={() =>
                  setDeleting({
                    url: p.image_url,
                    run: async () => {
                      await removeByPublicUrl("doctor-photos", p.image_url);
                      await supabase.from("doctor_photos").delete().eq("id", p.id);
                      setPhotos((x) => x.filter((y) => y.id !== p.id));
                      setDeleting(null);
                    },
                  })
                }
              >
                ×
              </button>
            </div>
          ))}
          {photos.length < DOCTOR_PHOTOS_MAX && (
            <label className="flex h-20 w-20 cursor-pointer items-center justify-center rounded-xl bg-slate-100 text-2xl text-muted">
              +
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={(e) => e.target.files?.[0] && onPhotoUpload(e.target.files[0])}
              />
            </label>
          )}
        </div>
      </Section>

      {/* Canvas assets */}
      <Section title={t("canvasAssets")}>
        {assetError && <p className="mb-3 text-sm text-red-600">{assetError}</p>}

        {/* Character sheet */}
        <label className="label">{t("characterSheet")}</label>
        <p className="mb-2 text-xs text-muted">{t("characterSheetHint")}</p>
        <div className="mb-4 flex items-center gap-3">
          {characterSheetUrl ? (
            <img
              src={characterSheetUrl}
              alt="character sheet"
              className="h-24 w-24 rounded-xl object-cover ring-1 ring-slate-100"
            />
          ) : (
            <div className="h-24 w-24 rounded-xl bg-slate-100" />
          )}
          <div className="flex flex-col gap-2">
            <button
              className="btn-ghost disabled:opacity-50"
              disabled={!!assetBusy || photos.length === 0}
              onClick={generateCharacterSheet}
            >
              {assetBusy === "charsheet" ? t("generating") : t("generateCharacterSheet")}
            </button>
            {characterSheetUrl && (
              <button
                className="text-sm text-red-500"
                onClick={() =>
                  setDeleting({
                    url: characterSheetUrl,
                    run: async () => {
                      await removeByPublicUrl("doctor-cutouts", characterSheetUrl);
                      await supabase
                        .from("profiles")
                        .update({ character_sheet_url: null })
                        .eq("id", uid);
                      setCharacterSheetUrl(null);
                      setDeleting(null);
                    },
                  })
                }
              >
                {t("remove")}
              </button>
            )}
          </div>
        </div>

        {/* Canvas backgrounds */}
        <label className="label">{t("canvasBackgrounds")}</label>
        <div className="mb-3 flex flex-wrap gap-2">
          {/* Fixed default (white→grey) — not deletable. */}
          <div
            className="flex h-20 w-20 items-center justify-center rounded-xl text-[10px] text-muted ring-1 ring-slate-200"
            style={{ background: "linear-gradient(135deg, #ffffff, #e5e7eb)" }}
          >
            {t("defaultBackground")}
          </div>
          {backgrounds.map((b) => (
            <div key={b.id} className="relative">
              <img src={b.image_url} alt="" className="h-20 w-20 rounded-xl object-cover" />
              <button
                className="absolute -top-2 -end-2 rounded-full bg-red-500 px-2 text-xs text-white"
                onClick={() =>
                  setDeleting({
                    url: b.image_url,
                    run: async () => {
                      await removeByPublicUrl("canvas-backgrounds", b.image_url);
                      await supabase.from("canvas_backgrounds").delete().eq("id", b.id);
                      setBackgrounds((x) => x.filter((y) => y.id !== b.id));
                      setDeleting(null);
                    },
                  })
                }
              >
                ×
              </button>
            </div>
          ))}
          <label className="flex h-20 w-20 cursor-pointer items-center justify-center rounded-xl bg-slate-100 text-2xl text-muted">
            +
            <input
              type="file"
              accept="image/*"
              hidden
              onChange={(e) => e.target.files?.[0] && onBackgroundUpload(e.target.files[0])}
            />
          </label>
        </div>
        <div className="flex gap-2">
          <input
            className="input"
            placeholder={t("backgroundPrompt")}
            value={bgPrompt}
            onChange={(e) => setBgPrompt(e.target.value)}
          />
          <button
            className="btn-ghost whitespace-nowrap px-4 disabled:opacity-50"
            disabled={!!assetBusy}
            onClick={generateBackground}
          >
            {assetBusy === "bg" ? t("generating") : t("generateBackground")}
          </button>
        </div>
      </Section>

      <ImageDeleteGuard
        open={!!deleting}
        imageUrl={deleting?.url ?? null}
        onCancel={() => setDeleting(null)}
        onDelete={() => deleting?.run()}
      />
    </div>
  );
}
